import { z } from 'zod'
import { MoneySchema } from './money'
import { OptionalTextSchema } from './text'
import { tolerantArray } from './tolerantArray'

/**
 * Cotação de frete do Maestro, fonte única: a PDP (`product/calc-product-shipping`) e o carrinho
 * (`cart/calc-shipping-maestro`) devolvem o mesmo `shipmentResponse` (snapshots Products/calc-product-shipping e
 * Cart/calc-shipping-maestro). O checkout não cota: cobra `cart.shippingTax`, calculado pela API no endereço de entrega.
 *
 * A cotação não tem id nem transportadora própria (`service` é o código do serviço) e o prazo vem em texto.
 *
 * Frete por vendedor (`ShippingBySellerSchema`): a `staging/api` do GlofiEcomm cota o carrinho por vendedor e deixa o
 * cliente escolher a opção de cada um (`cart/select-shipping`, `checkout/select-shipping`). O detalhamento chega em
 * `shipmentBySeller` no carrinho e em `shippingBySeller` no checkout; a `production/api` ainda não manda nenhum dos dois,
 * e sem ele a tela mostra as cotações do `shipmentResponse` como antes.
 */

const text = OptionalTextSchema

/** Prazo em dias, em texto (a API manda texto; cópias antigas, número). */
const daysText = z
    .union([z.string(), z.number()])
    .nullish()
    .transform((value) => (value == null ? '' : String(value)))

export const ShippingQuoteSchema = z.object({
    /** Prazo em dias, em texto. */
    days: z.union([z.string(), z.number()]).transform(String),
    location: z
        .union([z.string(), z.number()])
        .nullish()
        .transform((value) => (value == null ? '' : String(value))),
    name: text,
    price: MoneySchema,
    service: text,
    weight: z.number().nullish(),
})

/** `{ shipmentResponse: { quotes } }`, comum à PDP e ao carrinho; lista nula vira `[]`. */
export const ShippingQuotesResponseSchema = z.object({
    shipmentResponse: z.object({
        quotes: tolerantArray(ShippingQuoteSchema, 'cotações de frete'),
    }),
})

// ===== FRETE POR VENDEDOR =====

/** Opção de frete de um vendedor (`SellerShippingOptionResponse`). Sem `code` não há como escolher: sai da lista. */
export const SellerShippingOptionSchema = z.object({
    code: z.string().min(1),
    name: text,
    /** Slug da transportadora; nulo no provider legado (Maestro). */
    carrierCode: z
        .string()
        .nullish()
        .transform((value) => value ?? null),
    price: MoneySchema,
    days: daysText,
})

/**
 * Cotação de um vendedor (`SellerShippingQuote`). `price` e `days` são o frete valendo para o vendedor; sem `options`
 * (provider legado), são a única informação. `selectedOfferCode` é a opção valendo: a tela espelha este campo em vez de
 * guardar seleção própria. `shippingWasInvalidated`: a escolha do cliente deixou de valer e a API reaplicou a mais barata.
 */
export const SellerShippingQuoteSchema = z.object({
    sellerId: z.string().min(1),
    sellerName: text,
    price: MoneySchema,
    days: daysText,
    options: tolerantArray(SellerShippingOptionSchema, 'opções de frete do vendedor'),
    selectedOfferCode: z
        .string()
        .nullish()
        .transform((value) => value || null),
    shippingWasInvalidated: z
        .boolean()
        .nullish()
        .transform((value) => value ?? false),
})

/** Frete do carrinho por vendedor (`CartShippingBySellerResponse`): soma dos fretes e o maior prazo. */
export const ShippingBySellerSchema = z.object({
    sellers: tolerantArray(SellerShippingQuoteSchema, 'fretes por vendedor'),
    total: MoneySchema,
    deliveryDays: daysText,
})

/** O detalhamento por vendedor nas respostas: ausente (production/api) ou nulo vira `null`. */
export const OptionalShippingBySellerSchema = ShippingBySellerSchema.nullish().transform((value) => value ?? null)

/** Corpo do `cart/select-shipping` e do `checkout/select-shipping` (`SelectSellerShippingRequest`). */
export const SelectShippingRequestSchema = z.object({
    sellerId: z.string().min(1),
    offerCode: z.string().min(1),
})

export type IShippingQuote = z.infer<typeof ShippingQuoteSchema>
export type IShippingQuotesResponse = z.infer<typeof ShippingQuotesResponseSchema>
export type ISellerShippingOption = z.infer<typeof SellerShippingOptionSchema>
export type ISellerShippingQuote = z.infer<typeof SellerShippingQuoteSchema>
export type IShippingBySeller = z.infer<typeof ShippingBySellerSchema>
export type ISelectShippingRequest = z.infer<typeof SelectShippingRequestSchema>
